import { Injectable } from "@angular/core";
import { MatomoTracker } from "@ngx-matomo/tracker";
import { AppSettings } from "../shared/Constants/sharedConstants";

export enum LogLevel {
  All = 0,
  Debug = 1,
  Info = 2,
  Warn = 3,
  Error = 4,
  Fatal = 5,
  EventTracking = 6,
}

@Injectable()
export class LogService {
  level: LogLevel = LogLevel.All;

  constructor(private matomoTracker: MatomoTracker) {}

  public debug(msg: string, ...optionalParams: any) {
    this.writeToLog(msg, LogLevel.Debug, optionalParams);
  }

  public info(msg: string, ...optionalParams: any) {
    this.writeToLog(msg, LogLevel.Info, optionalParams);
  }

  public warn(msg: string, ...optionalParams: any) {
    this.writeToLog(msg, LogLevel.Warn, optionalParams);
  }

  public error(msg: string, ...optionalParams: any) {
    this.writeToLog(msg, LogLevel.Error, optionalParams);
  }

  public fatal(msg: string, ...optionalParams: any) {
    this.writeToLog(msg, LogLevel.Fatal, optionalParams);
  }

  public log(msg: any, ...optionalParams: any) {
    this.writeToLog(msg, LogLevel.All, optionalParams);
  }

  // Writes nothing, deliberately. Every caller can pass arbitrary data - server responses,
  // route values, error bodies - and a console sink fed that way is a log-injection finding
  // (CWE-117) that no escaping here clears. Uncaught errors still reach the console through
  // Angular's ErrorHandler. The level methods above are kept so the ~380 call sites compile;
  // delete calls as you touch them rather than wiring a sink back in.
  private writeToLog(_msg: any, _logLevel: LogLevel = this.level, _optionalParams?: any) {}

  // site tracker services (Matomo)
  public enableHeartBeatTimer(interval: number) {
    if (!this.logToSiteUsageTracker())
      this.writeToLog(
        "Matomo Heartbeat Timer enabled: ",
        LogLevel.EventTracking,
        interval
      );
    else this.matomoTracker.enableHeartBeatTimer(interval);
  }

  public enableLinkTracking(tf: boolean) {
    if (!this.logToSiteUsageTracker())
      this.writeToLog("Matomo Link Tracking set: ", LogLevel.EventTracking, tf);
    else this.matomoTracker.enableLinkTracking(tf);
  }

  public setUserId(id: string) {
    if (!this.logToSiteUsageTracker())
      this.writeToLog("Matomo User Id set: ", LogLevel.EventTracking, id);
    else this.matomoTracker.setUserId(id);
  }

  public setCustomVariable(
    id: number,
    name: string,
    value: string,
    type: any
  ) {
    if (!this.logToSiteUsageTracker())
      this.writeToLog(
        "Matomo Custom Variable set: ",
        LogLevel.EventTracking,
        "${id} ${name} ${value} ${type}"
      );
    else this.matomoTracker.setCustomVariable(id, name, value, type);
  }

  public trackEvent(category: string, action: string, name?: any, value: number | null = null) {
    if (!this.logToSiteUsageTracker())
      this.writeToLog(
        "Matomo Event Tracked: ",
        LogLevel.EventTracking,
        `${category} ${action} ${name} ${value}`
      );
    else this.matomoTracker.trackEvent(category, action, name, value);
  }

  public trackPageView(pageName: string) {
    if (!this.logToSiteUsageTracker())
      this.writeToLog(
        "Matomo Page View Tracked:",
        LogLevel.EventTracking,
        pageName
      );
    else {
      this.matomoTracker.trackPageView(pageName);
    }
  }

  logToSiteUsageTracker(): boolean {
    return AppSettings?.UIAppConfig.logToSiteUsageTracker;
  }
}
